<!-- eslint-disable vue/no-v-html -->
<template>
  <AModal
    :model-value="visible"
    :size="mode === 'preview' ? 'full' : 'xl'"
    body-flush
    @update:model-value="onModalUpdate"
    @close="$emit('close')"
  >
    <template #title>
      <span class="edm__title">
        <i class="material-icons-round edm__title-icon">assignment</i>
        {{ mode === 'grading' ? '시험 상세 정보' : '시험 미리보기' }}
      </span>
    </template>

    <div :class="['edm__body', { 'edm__body--split': mode === 'preview' }]">
      <!-- Preview 모드: 좌우 분할 -->
      <template v-if="mode === 'preview'">
        <!-- 좌측: 기본 정보 + 문항 분포 + 시험 설정 -->
        <aside class="edm__left">
          <section class="edm__section">
            <header class="edm__section-head">
              <i class="material-icons-round edm__section-icon">info</i>
              <span class="edm__section-title">기본 정보</span>
            </header>
            <div class="edm__info-grid">
              <div class="edm__info-item">
                <span class="edm__info-label">시험명</span>
                <span class="edm__info-value">{{ exam?.examName || '-' }}</span>
              </div>
              <div class="edm__info-item">
                <span class="edm__info-label">설명</span>
                <span class="edm__info-value">{{ exam?.description || '-' }}</span>
              </div>
              <div class="edm__info-item">
                <span class="edm__info-label">시험 시간</span>
                <span class="edm__info-value edm__info-value--mono">{{ exam?.duration || 0 }}분</span>
              </div>
              <div class="edm__info-item">
                <span class="edm__info-label">총 문항 수</span>
                <span class="edm__info-value edm__info-value--mono">{{ totalQuestions }}문항</span>
              </div>
              <div class="edm__info-item">
                <span class="edm__info-label">합격 점수</span>
                <span class="edm__info-value edm__info-value--mono">{{ exam?.passingScore || 60 }}점</span>
              </div>
              <div class="edm__info-item">
                <span class="edm__info-label">시작 시간</span>
                <span class="edm__info-value">{{ formatDateTime(exam?.startTimeUTC, exam?.timeZone) }}</span>
              </div>
              <div class="edm__info-item">
                <span class="edm__info-label">종료 시간</span>
                <span class="edm__info-value">{{ formatDateTime(exam?.endTimeUTC, exam?.timeZone) }}</span>
              </div>
              <div class="edm__info-item">
                <span class="edm__info-label">타임존</span>
                <span class="edm__info-value edm__info-value--mono">{{ exam?.timeZone || 'Asia/Seoul' }}</span>
              </div>
            </div>
          </section>

          <section class="edm__section">
            <header class="edm__section-head">
              <i class="material-icons-round edm__section-icon">pie_chart</i>
              <span class="edm__section-title">문항 분포</span>
            </header>
            <div class="edm__dist">
              <div
                v-for="(dist, skill) in questionDistribution"
                :key="skill"
                class="edm__dist-item"
              >
                <div class="edm__dist-head">
                  <span class="edm__dist-name">{{ getSkillDisplayName(skill) }}</span>
                  <span class="edm__dist-count">{{ dist.count }}문항</span>
                </div>
                <div class="edm__dist-bar">
                  <div
                    :class="['edm__dist-fill', `edm__dist-fill--${skill}`]"
                    :style="{ width: getDistributionPercentage(dist.count) + '%' }"
                  ></div>
                </div>
                <div class="edm__dist-pct">{{ getDistributionPercentage(dist.count).toFixed(1) }}%</div>
              </div>
            </div>
          </section>

          <section v-if="exam?.settings" class="edm__section">
            <header class="edm__section-head">
              <i class="material-icons-round edm__section-icon">settings</i>
              <span class="edm__section-title">시험 설정</span>
            </header>
            <div class="edm__settings">
              <div :class="['edm__setting', exam.settings.faceToFace ? 'is-on' : 'is-off']">
                <i class="material-icons-round">{{ exam.settings.faceToFace ? 'check_circle' : 'cancel' }}</i>
                <span>대면 시험</span>
              </div>
              <!-- A/B 버전(settings.generateABVersion). 백엔드 설정에 없는 randomizeQuestions(늘 ✗)를 보이던 자리 -->
              <div :class="['edm__setting', exam.settings.generateABVersion ? 'is-on' : 'is-off']">
                <i class="material-icons-round">{{ exam.settings.generateABVersion ? 'check_circle' : 'cancel' }}</i>
                <span>A/B 버전</span>
              </div>
              <div :class="['edm__setting', exam.settings.showResults ? 'is-on' : 'is-off']">
                <i class="material-icons-round">{{ exam.settings.showResults ? 'check_circle' : 'cancel' }}</i>
                <span>결과 즉시 표시</span>
              </div>
            </div>
          </section>
        </aside>

        <!-- 우측: 문항 상세 -->
        <main class="edm__right">
          <section class="edm__section">
            <header class="edm__section-head">
              <i class="material-icons-round edm__section-icon">quiz</i>
              <span class="edm__section-title">문항 상세 정보</span>
              <!-- 버전 탭 — 버전이 둘 이상일 때만. 탭의 숫자와 「총 N문항」은 그 버전의 문항 수 -->
              <div v-if="hasVersionTabs" class="p-tabs edm__version-tabs" role="tablist">
                <button
                  v-for="version in versions"
                  :key="version.key"
                  type="button"
                  role="tab"
                  :aria-selected="version.key === activeVersion?.key ? 'true' : 'false'"
                  :class="['p-tabs__tab', { 'is-active': version.key === activeVersion?.key }]"
                  @click="activeVersionKey = version.key"
                >
                  {{ version.label }}
                  <span class="p-tabs__count">{{ (version.questions || []).length }}</span>
                </button>
              </div>
              <APill v-if="totalQuestionsFromGroups > 0" kind="yellow" class="edm__section-extra">
                총 {{ totalQuestionsFromGroups }}문항
              </APill>
            </header>

            <p v-if="hasVersionTabs" class="edm__version-note">
              <i class="material-icons-round">info</i>
              <span>B형은 A형과 같은 문항·배점이며 문항 단위로 순서만 섞여 기능·그룹이 흩어집니다. 각 버전의 실제 순서대로 그립니다.</span>
            </p>

            <!-- Question Groups 구조 — 블록은 그 버전의 순서에서 같은 그룹이 이어지는 구간마다 하나(B형은 한 그룹이 여러 블록) -->
            <div v-if="displayGroups && displayGroups.length > 0" class="edm__groups">
              <article
                v-for="(group, groupIndex) in displayGroups"
                :key="group.blockKey || group._id || group.groupId || groupIndex"
                class="edm__group"
              >
                <header class="edm__group-head">
                  <APill :kind="getSkillPillKind(group.skill)" dot>
                    {{ getSkillDisplayName(group.skill) || '미지정' }}
                  </APill>
                  <span v-if="group.textBook" class="edm__group-textbook">{{ group.textBook }}</span>
                </header>

                <div v-if="group.instruction" class="edm__group-section">
                  <div class="edm__sub-label"><i class="material-icons-round">description</i> 지시사항</div>
                  <div class="edm__rich" v-html="group.instruction"></div>
                </div>

                <div v-if="group.example" class="edm__group-section edm__group-section--example">
                  <div class="edm__sub-label"><i class="material-icons-round">lightbulb</i> 보기</div>
                  <div class="edm__rich" v-html="group.example"></div>
                </div>

                <div v-if="hasHtmlContent(group.readingScript)" class="edm__group-section edm__group-section--reading">
                  <div class="edm__sub-label"><i class="material-icons-round">auto_stories</i> 읽기 지문</div>
                  <div class="edm__rich edm__rich--reading" v-html="group.readingScript"></div>
                </div>

                <div v-if="group.groupAudio && (group.groupAudio.file || group.groupAudio.script)" class="edm__group-section">
                  <div class="edm__sub-label"><i class="material-icons-round">headphones</i> 오디오 정보</div>
                  <template v-if="group.groupAudio.file">
                    <div class="edm__audio">
                      <i class="material-icons-round edm__audio-icon">audio_file</i>
                      <span class="edm__audio-name">{{ group.groupAudio.file }}</span>
                      <ABtn
                        v-if="!audioUrls[group.groupAudio.file]"
                        size="sm"
                        :icon="loadingAudio[group.groupAudio.file] ? 'hourglass_empty' : 'play_circle'"
                        :loading="!!loadingAudio[group.groupAudio.file]"
                        @click="loadAudio(group.groupAudio.file)"
                      >{{ loadingAudio[group.groupAudio.file] ? '로딩중...' : '듣기' }}</ABtn>
                    </div>
                    <div v-if="audioUrls[group.groupAudio.file]" class="edm__audio-player">
                      <audio controls :src="audioUrls[group.groupAudio.file]" preload="metadata">
                        브라우저가 오디오 재생을 지원하지 않습니다.
                      </audio>
                    </div>
                  </template>
                  <div v-if="group.groupAudio.script" class="edm__audio-script">
                    <span class="edm__audio-script-label">스크립트</span>
                    <span>{{ group.groupAudio.script }}</span>
                  </div>
                </div>

                <!-- 동영상 (groupVideos) — 플레이어 목록(VideoEmbed, 바로 그림), 모든 스킬. 이 필드가 생기기 전 시험의 스냅샷에는 키가 없다 -->
                <div v-if="getGroupVideos(group).length > 0" class="edm__group-section">
                  <div class="edm__sub-label"><i class="material-icons-round">videocam</i> 동영상 {{ getGroupVideos(group).length }}개</div>
                  <div class="edm__videos">
                    <template v-for="(video, vi) in getGroupVideos(group)" :key="vi">
                      <VideoEmbed v-if="isValidVideoUrl(video.url)" :url="video.url" :title="`동영상 ${vi + 1}`" />
                      <div v-else class="edm__video-bad">
                        <i class="material-icons-round">videocam_off</i>
                        <span>{{ video.url }}</span>
                      </div>
                    </template>
                  </div>
                </div>

                <!-- 그룹 내 문제들 -->
                <div class="edm__questions">
                  <template v-if="Array.isArray(group.questions)">
                    <div
                      v-for="(question, qIndex) in group.questions"
                      :key="question.questionId || qIndex"
                      class="edm__question"
                    >
                      <div class="edm__q-head">
                        <!-- 번호는 버전 안에서 1부터 이어 센다(블록이 바뀌어도 다시 세지 않음) -->
                        <APill kind="dark">문제 {{ question.number || qIndex + 1 }}</APill>
                        <APill :kind="levelPillKind(question.level)">{{ getLevelDisplayName(question.level) }}</APill>
                        <span class="edm__q-score">{{ question.score || 0 }}점</span>
                      </div>
                      <div class="edm__q-body">
                        <div v-if="question.questionAudio?.file" class="edm__qaudio">
                          <div class="edm__audio edm__audio--compact">
                            <i class="material-icons-round edm__audio-icon">music_note</i>
                            <span class="edm__audio-name">{{ question.questionAudio.file }}</span>
                            <ABtn
                              v-if="!audioUrls[question.questionAudio.file]"
                              size="sm"
                              :icon="loadingAudio[question.questionAudio.file] ? 'hourglass_empty' : 'play_circle'"
                              :loading="!!loadingAudio[question.questionAudio.file]"
                              @click="loadAudio(question.questionAudio.file)"
                            >{{ loadingAudio[question.questionAudio.file] ? '로딩...' : '듣기' }}</ABtn>
                          </div>
                          <div v-if="audioUrls[question.questionAudio.file]" class="edm__audio-player edm__audio-player--compact">
                            <audio controls :src="audioUrls[question.questionAudio.file]" preload="metadata">
                              브라우저가 오디오 재생을 지원하지 않습니다.
                            </audio>
                          </div>
                          <div v-if="question.questionAudio.script" class="edm__audio-script edm__audio-script--compact">
                            <span class="edm__audio-script-label">스크립트</span>
                            <span>{{ question.questionAudio.script }}</span>
                          </div>
                        </div>
                        <div class="edm__q-text" v-html="question.questionText || question.content"></div>
                        <div v-if="getChoicesArray(question.choices).length > 0" class="edm__choices">
                          <div
                            v-for="(choice, choiceIndex) in getChoicesArray(question.choices)"
                            :key="choiceIndex"
                            :class="['edm__choice', { 'is-correct': choice.isAnswer || choice.correct, 'has-image': choice.image && choice.image.file }]"
                          >
                            <span class="edm__choice-num">{{ choiceIndex + 1 }})</span>
                            <div class="edm__choice-content">
                              <img
                                v-if="choice.image && choice.image.file"
                                :src="getChoiceImageUrl(choice.image.file)"
                                :alt="`선택지 ${choiceIndex + 1} 이미지`"
                                class="edm__choice-img"
                                @error="handleImageError"
                              />
                              <span v-if="choice.choiceText || choice.text" class="edm__choice-text">
                                {{ choice.choiceText || choice.text || choice }}
                              </span>
                            </div>
                            <i v-if="choice.isAnswer || choice.correct" class="material-icons-round edm__choice-check">check_circle</i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-else-if="group.questions && typeof group.questions === 'object'">
                    <div class="edm__question">
                      <div class="edm__q-head">
                        <APill kind="dark">문제 1</APill>
                        <APill :kind="levelPillKind(group.questions.level)">{{ getLevelDisplayName(group.questions.level) }}</APill>
                        <span class="edm__q-score">{{ group.questions.score || 0 }}점</span>
                      </div>
                      <div class="edm__q-body">
                        <div v-if="group.questions.questionAudio?.file" class="edm__qaudio">
                          <div class="edm__audio edm__audio--compact">
                            <i class="material-icons-round edm__audio-icon">music_note</i>
                            <span class="edm__audio-name">{{ group.questions.questionAudio.file }}</span>
                            <ABtn
                              v-if="!audioUrls[group.questions.questionAudio.file]"
                              size="sm"
                              :icon="loadingAudio[group.questions.questionAudio.file] ? 'hourglass_empty' : 'play_circle'"
                              :loading="!!loadingAudio[group.questions.questionAudio.file]"
                              @click="loadAudio(group.questions.questionAudio.file)"
                            >{{ loadingAudio[group.questions.questionAudio.file] ? '로딩...' : '듣기' }}</ABtn>
                          </div>
                          <div v-if="audioUrls[group.questions.questionAudio.file]" class="edm__audio-player edm__audio-player--compact">
                            <audio controls :src="audioUrls[group.questions.questionAudio.file]" preload="metadata">
                              브라우저가 오디오 재생을 지원하지 않습니다.
                            </audio>
                          </div>
                          <div v-if="group.questions.questionAudio.script" class="edm__audio-script edm__audio-script--compact">
                            <span class="edm__audio-script-label">스크립트</span>
                            <span>{{ group.questions.questionAudio.script }}</span>
                          </div>
                        </div>
                        <div class="edm__q-text" v-html="group.questions.questionText || group.questions.content || '문제 내용'"></div>
                        <div v-if="getChoicesArray(group.questions.choices).length > 0" class="edm__choices">
                          <div
                            v-for="(choice, choiceIndex) in getChoicesArray(group.questions.choices)"
                            :key="choiceIndex"
                            :class="['edm__choice', { 'is-correct': choice.isAnswer || choice.correct, 'has-image': choice.image && choice.image.file }]"
                          >
                            <span class="edm__choice-num">{{ choiceIndex + 1 }})</span>
                            <div class="edm__choice-content">
                              <img
                                v-if="choice.image && choice.image.file"
                                :src="getChoiceImageUrl(choice.image.file)"
                                :alt="`선택지 ${choiceIndex + 1} 이미지`"
                                class="edm__choice-img"
                                @error="handleImageError"
                              />
                              <span v-if="choice.choiceText || choice.text" class="edm__choice-text">
                                {{ choice.choiceText || choice.text || choice }}
                              </span>
                            </div>
                            <i v-if="choice.isAnswer || choice.correct" class="material-icons-round edm__choice-check">check_circle</i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </article>
            </div>

            <!-- Fallback: 그룹 정보가 없어 문항 목록만 있는 경우 -->
            <div v-else-if="displayQuestions && displayQuestions.length > 0" class="edm__questions">
              <article
                v-for="(question, index) in displayQuestions"
                :key="question.questionId || index"
                class="edm__question"
              >
                <div class="edm__q-head">
                  <APill kind="dark">문제 {{ question.number || index + 1 }}</APill>
                  <APill :kind="getSkillPillKind(question.skill)" dot>
                    {{ getSkillDisplayName(question.skill) || '미지정' }}
                  </APill>
                  <APill :kind="levelPillKind(question.level)">{{ getLevelDisplayName(question.level) }}</APill>
                  <span class="edm__q-score">{{ question.score || 0 }}점</span>
                </div>
                <div class="edm__q-body">
                  <div class="edm__q-text" v-html="question.questionText || question.content || '문제 내용'"></div>
                  <div v-if="getChoicesArray(question.choices).length > 0" class="edm__choices">
                    <div
                      v-for="(choice, choiceIndex) in getChoicesArray(question.choices)"
                      :key="choiceIndex"
                      :class="['edm__choice', { 'is-correct': choice.isAnswer || choice.correct, 'has-image': choice.image && choice.image.file }]"
                    >
                      <span class="edm__choice-num">{{ choiceIndex + 1 }})</span>
                      <div class="edm__choice-content">
                        <img
                          v-if="choice.image && choice.image.file"
                          :src="getChoiceImageUrl(choice.image.file)"
                          :alt="`선택지 ${choiceIndex + 1} 이미지`"
                          class="edm__choice-img"
                          @error="handleImageError"
                        />
                        <span v-if="choice.choiceText || choice.text" class="edm__choice-text">
                          {{ choice.choiceText || choice.text || choice }}
                        </span>
                      </div>
                      <i v-if="choice.isAnswer || choice.correct" class="material-icons-round edm__choice-check">check_circle</i>
                    </div>
                  </div>
                </div>
              </article>
            </div>

            <AEmpty
              v-else
              title="등록된 문항이 없어요"
              sub="문항 정보를 불러오는 중이거나 등록된 문항이 없습니다."
            />
          </section>
        </main>
      </template>

      <!-- Grading 모드: 단일 컨테이너 -->
      <div v-else class="edm__regular">
        <section class="edm__section">
          <header class="edm__section-head">
            <i class="material-icons-round edm__section-icon">info</i>
            <span class="edm__section-title">기본 정보</span>
          </header>
          <div class="edm__info-grid">
            <div class="edm__info-item">
              <span class="edm__info-label">시험명</span>
              <span class="edm__info-value">{{ exam?.examName || '-' }}</span>
            </div>
            <div class="edm__info-item">
              <span class="edm__info-label">설명</span>
              <span class="edm__info-value">{{ exam?.description || '-' }}</span>
            </div>
            <div class="edm__info-item">
              <span class="edm__info-label">시험 시간</span>
              <span class="edm__info-value edm__info-value--mono">{{ exam?.duration || 0 }}분</span>
            </div>
            <div class="edm__info-item">
              <span class="edm__info-label">총 문항 수</span>
              <span class="edm__info-value edm__info-value--mono">{{ totalQuestions }}문항</span>
            </div>
          </div>
        </section>

        <section v-if="mode === 'grading' && statistics" class="edm__section">
          <header class="edm__section-head">
            <i class="material-icons-round edm__section-icon">analytics</i>
            <span class="edm__section-title">채점 통계</span>
          </header>
          <div class="edm__stats">
            <ACard><AStat label="전체 학생" :value="formatNum(statistics.totalStudents)" unit="명" /></ACard>
            <ACard><AStat label="제출 완료" :value="formatNum(statistics.completedCount)" unit="명" /></ACard>
            <ACard><AStat label="평균 점수" :value="(statistics.averageScore || 0).toFixed(1)" /></ACard>
            <ACard><AStat label="합격률" :value="(statistics.passRate || 0).toFixed(1)" unit="%" /></ACard>
          </div>
        </section>
      </div>
    </div>

    <template v-if="showActions" #foot>
      <slot name="actions"></slot>
    </template>
  </AModal>
</template>

<script>
import { computed, reactive, ref, watch, getCurrentInstance } from 'vue'
import { formatUtcToTimeZone } from '@/utils/datetime'
import { hasHtmlContent } from '@/utils/text'
import { normalizeGroupVideos, isValidVideoUrl } from '@/utils/groupVideos'
import Config from '@/Config'
import VideoEmbed from '@/components/VideoEmbed.vue'
import AModal from '@/components/Pineapple/AModal.vue'
import APill from '@/components/Pineapple/APill.vue'
import ABtn from '@/components/Pineapple/ABtn.vue'
import AEmpty from '@/components/Pineapple/AEmpty.vue'
import AStat from '@/components/Pineapple/AStat.vue'
import ACard from '@/components/Pineapple/ACard.vue'

export default {
  name: 'ExamDetailModal',
  components: { AModal, APill, ABtn, AEmpty, AStat, ACard, VideoEmbed },
  props: {
    visible: { type: Boolean, default: false },
    exam: { type: Object, default: () => ({}) },
    mode: {
      type: String,
      default: 'preview',
      validator: (v) => ['preview', 'grading'].includes(v),
    },
    statistics: { type: Object, default: null },
    showQuestions: { type: Boolean, default: false },
    showActions: { type: Boolean, default: false },
    answers: { type: Array, default: () => [] },
    questions: { type: Array, default: () => [] },
    questionGroups: { type: Array, default: () => [] },
    // 버전별 문항 — [{ key: 'A', label: 'A형', questions, questionGroups }, ...] (ExamList 가 만든다).
    // 있으면 questions · questionGroups 대신 쓰고, 둘 이상이면 「A형 / B형」 탭으로 보인다
    versions: { type: Array, default: () => [] },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const instance = getCurrentInstance()

    const audioUrls = reactive({})
    const loadingAudio = reactive({})

    const loadAudio = async (fileName) => {
      if (!fileName || audioUrls[fileName]) return
      loadingAudio[fileName] = true
      try {
        const blobUrl = await instance.proxy.$getAudioStreamUrl(fileName, 'exam_files', Config.ACHIEVE_API_URL)
        if (blobUrl) audioUrls[fileName] = blobUrl
      } catch (error) {
        console.error('오디오 로드 실패:', error)
      } finally {
        loadingAudio[fileName] = false
      }
    }

    const onModalUpdate = (value) => {
      if (!value) emit('close')
    }

    const totalQuestions = computed(() => {
      if (typeof props.exam?.totalQuestions === 'number') return props.exam.totalQuestions
      if (!props.exam?.questionDistribution) return 0
      return Object.values(props.exam.questionDistribution).reduce((sum, dist) => sum + (dist?.count || 0), 0)
    })

    // ── 버전 탭 ── 모달을 열 때(그리고 버전 목록이 바뀔 때) 첫 버전(A형)으로 돌아간다
    const activeVersionKey = ref(props.versions?.[0]?.key || '')
    const resetActiveVersion = () => {
      activeVersionKey.value = props.versions?.[0]?.key || ''
    }
    watch(() => props.visible, (visible) => { if (visible) resetActiveVersion() })
    watch(() => props.versions, resetActiveVersion)

    const hasVersionTabs = computed(() => (props.versions || []).length > 1)
    // 고른 키가 목록에 없으면(열기 전·목록 교체 직후) 첫 버전으로 본다 — 탭 표시와 그리는 문항이 같은 버전을 가리키게
    const activeVersion = computed(() =>
      (props.versions || []).find((version) => version.key === activeVersionKey.value) || props.versions?.[0] || null
    )
    // 그리는 문항 — 버전이 있으면 고른 버전의 것, 없으면 questions · questionGroups 그대로
    const displayQuestions = computed(() => (activeVersion.value ? activeVersion.value.questions || [] : props.questions || []))
    const displayGroups = computed(() => (activeVersion.value ? activeVersion.value.questionGroups || [] : props.questionGroups || []))

    const totalQuestionsFromGroups = computed(() => {
      if (!displayGroups.value || displayGroups.value.length === 0) return displayQuestions.value?.length || 0
      return displayGroups.value.reduce((sum, group) => {
        if (Array.isArray(group.questions)) return sum + group.questions.length
        if (group.questions && typeof group.questions === 'object') return sum + 1
        return sum
      }, 0)
    })

    const questionDistribution = computed(() => props.exam?.questionDistribution || {})

    const getSkillDisplayName = (skill) => ({
      reading: '읽기',
      grammar: '문법',
      vocabulary: '어휘',
      listening: '듣기',
      speaking: '말하기',
      writing: '쓰기',
    })[skill] || skill

    const getLevelDisplayName = (level) => ({ low: '하', middle: '중', high: '상' })[level] || level || '중'

    const getSkillPillKind = (skill) => ({
      reading: 'info',
      grammar: 'ok',
      vocabulary: 'info',
      listening: 'warn',
      speaking: 'err',
      writing: 'default',
    })[skill] || 'default'

    const levelPillKind = (level) => ({ low: 'ok', middle: 'warn', high: 'err' })[level] || 'default'

    // 그룹 동영상 URL 목록 — 스냅샷이 문항마다 담는다(2026-10-12, 항상 배열). 그 전 시험은 키가 없어 [] 로
    const getGroupVideos = (group) => normalizeGroupVideos(group?.groupVideos)

    const getChoicesArray = (choices) => {
      if (!choices) return []
      if (Array.isArray(choices)) return choices
      if (typeof choices === 'object') return [choices]
      return []
    }

    const getChoiceImageUrl = (fileName) => fileName ? `${Config.ACHIEVE_API_URL}/files/image/stream/${fileName}` : ''

    const handleImageError = (event) => {
      console.error('이미지 로드 실패:', event.target.src)
      event.target.style.display = 'none'
    }

    const getDistributionPercentage = (count) => {
      const total = totalQuestions.value
      if (total === 0) return 0
      return (count / total) * 100
    }

    const formatDateTime = (utcTime, timeZone) => {
      if (!utcTime) return '-'
      try {
        const tz = timeZone || 'Asia/Seoul'
        const localTime = formatUtcToTimeZone(utcTime, tz)
        if (!localTime) return '-'
        const date = new Date(localTime)
        return date.toLocaleString('ko-KR', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit',
        })
      } catch {
        return utcTime
      }
    }

    const formatNum = (n) => Number(n || 0).toLocaleString()

    return {
      onModalUpdate,
      totalQuestions,
      totalQuestionsFromGroups,
      // 버전 탭
      activeVersionKey,
      activeVersion,
      hasVersionTabs,
      displayQuestions,
      displayGroups,
      questionDistribution,
      getSkillDisplayName,
      getLevelDisplayName,
      getSkillPillKind,
      levelPillKind,
      getChoicesArray,
      getChoiceImageUrl,
      handleImageError,
      getDistributionPercentage,
      formatDateTime,
      formatNum,
      hasHtmlContent,
      audioUrls,
      loadingAudio,
      loadAudio,
      // 그룹 동영상
      getGroupVideos,
      isValidVideoUrl,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.edm {
  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 800;
    color: $p-ink;
  }

  &__title-icon {
    color: $p-point-deep;
    font-size: 20px !important;
  }

  // ── 본문 ──
  &__body {
    background: $p-bg;
    padding: 18px 22px 22px;

    &--split {
      display: grid;
      grid-template-columns: minmax(320px, 1fr) 2fr;
      gap: 14px;
      padding: 16px;

      @media (max-width: 1100px) {
        grid-template-columns: 1fr;
      }
    }
  }

  &__left,
  &__right {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__regular {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  // ── 섹션 ──
  &__section {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-lg;
    padding: 16px 18px;
  }

  &__section-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid $p-line-soft;
  }

  &__section-icon {
    color: $p-point-deep;
    font-size: 16px !important;
  }

  &__section-title {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: $p-ink2;
  }

  &__section-extra {
    margin-left: auto;
  }

  // ── 버전 탭(A형 / B형) ──
  &__version-tabs {
    margin-left: 6px;
  }

  &__version-note {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: -4px 0 12px;
    font-size: 11px;
    line-height: 1.5;
    color: $p-ink-mid;

    > i {
      flex-shrink: 0;
      margin-top: 1px;
      font-size: 14px !important;
      color: $p-info;
    }
  }

  // ── 정보 그리드 ──
  &__info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;
  }

  &__info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
  }

  &__info-label {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: $p-ink-soft;
  }

  &__info-value {
    font-size: 13px;
    color: $p-ink;
    font-weight: 700;

    &--mono { font-family: $p-font-mono; }
  }

  // ── 분포 ──
  &__dist {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__dist-item {
    padding: 10px 12px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
  }

  &__dist-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 6px;
  }

  &__dist-name {
    font-size: 12px;
    font-weight: 700;
    color: $p-ink;
  }

  &__dist-count {
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-point-deep;
    font-weight: 800;
  }

  &__dist-bar {
    height: 6px;
    background: $p-line-soft;
    border-radius: 3px;
    overflow: hidden;
  }

  &__dist-fill {
    height: 100%;
    background: $p-ink;
    border-radius: 3px;

    &--reading    { background: $p-info; }
    &--grammar    { background: $p-ok; }
    &--vocabulary { background: $p-info; }
    &--listening  { background: $p-warn; }
    &--speaking   { background: $p-err; }
    &--writing    { background: $p-ink-mid; }
  }

  &__dist-pct {
    margin-top: 4px;
    font-size: 10px;
    color: $p-ink-soft;
    text-align: right;
    font-family: $p-font-mono;
  }

  // ── 설정 ──
  &__settings {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__setting {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-radius: $p-radius-md;
    font-size: 12px;
    color: $p-ink2;
    border: 1px solid $p-line-soft;
    background: $p-panel-alt;

    > i { font-size: 16px !important; }

    &.is-on {
      color: $p-ok;
      background: $p-ok-soft;
      border-color: lighten($p-ok, 32%);
    }

    &.is-off {
      color: $p-ink-soft;
    }
  }

  // ── 통계 카드 (grading 모드) ──
  &__stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }

  // ── 그룹 ──
  &__groups {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__group {
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
    padding: 14px 16px;
  }

  &__group-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 10px;
    margin-bottom: 12px;
    border-bottom: 1px solid $p-line-soft;
  }

  &__group-textbook {
    font-size: 11px;
    color: $p-ink-soft;
    font-style: italic;
  }

  &__group-section {
    margin-bottom: 12px;

    &:last-child { margin-bottom: 0; }
  }

  &__sub-label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: $p-point-deep;
    margin-bottom: 6px;

    > i { font-size: 13px !important; }
  }

  &__rich {
    background: $p-panel;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
    padding: 10px 12px;
    font-size: 12px;
    line-height: 1.65;
    color: $p-ink;

    :deep(p) { margin: 0 0 8px; &:last-child { margin-bottom: 0; } }
    :deep(img) {
      max-width: 100%;
      max-height: 240px;
      object-fit: contain;
      border-radius: $p-radius-sm;
      display: block;
      margin: 6px 0;
    }

    &--reading {
      background: $p-warn-soft;
      border-color: $p-warn;
      border-left: 3px solid $p-warn;
      white-space: pre-wrap;
    }
  }

  // ── 오디오 ──
  &__audio {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: $p-panel;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;

    &--compact { padding: 8px 10px; }
  }

  &__audio-icon {
    color: $p-point-deep;
    font-size: 18px !important;
  }

  &__audio-name {
    flex: 1;
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink2;
    word-break: break-all;
  }

  &__audio-player {
    margin-top: 6px;
    background: $p-bg;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
    padding: 10px 12px;

    audio { width: 100%; height: 36px; }

    &--compact {
      padding: 8px 10px;
      audio { height: 32px; }
    }
  }

  &__audio-script {
    margin-top: 6px;
    padding: 8px 10px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-sm;
    font-size: 11px;
    color: $p-ink2;
    line-height: 1.5;
    display: flex;
    gap: 6px;

    &--compact { font-size: 10px; padding: 6px 10px; }
  }

  &__audio-script-label {
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: $p-point-deep;
    flex-shrink: 0;
    align-self: center;
  }

  // ── 동영상 ──
  &__videos {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__video-bad {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 12px;
    background: $p-err-soft;
    border: 1px solid $p-err;
    border-radius: $p-radius-md;
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-err-ink;
    word-break: break-all;

    > i { font-size: 16px !important; flex-shrink: 0; }
  }

  // ── 문제 ──
  &__questions {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__question {
    background: $p-panel;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
    padding: 12px 14px;
  }

  &__q-head {
    display: flex;
    align-items: center;
    gap: 6px;
    padding-bottom: 8px;
    margin-bottom: 10px;
    border-bottom: 1px solid $p-line-soft;
  }

  &__q-score {
    margin-left: auto;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    color: $p-point-deep;
    background: $p-point-soft;
    border: 1px solid $p-point-deep;
    padding: 3px 8px;
    border-radius: 5px;
  }

  &__q-body {
    padding-left: 4px;
  }

  &__qaudio {
    margin-bottom: 10px;
  }

  &__q-text {
    font-size: 13px;
    line-height: 1.65;
    color: $p-ink;
    margin-bottom: 10px;

    :deep(p) { margin: 0 0 8px; &:last-child { margin-bottom: 0; } }
    :deep(img) {
      max-width: 100%;
      max-height: 240px;
      object-fit: contain;
      border-radius: $p-radius-sm;
      display: block;
      margin: 6px 0;
    }
  }

  // ── 선택지 ──
  &__choices {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__choice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
    transition: border-color $p-dur $p-ease;

    &:hover { border-color: $p-line; }

    &.is-correct {
      background: $p-ok-soft;
      border-color: $p-ok;
    }

    &.has-image {
      flex-direction: column;
      align-items: flex-start;
      position: relative;
    }
  }

  &__choice-num {
    font-family: $p-font-mono;
    font-weight: 800;
    color: $p-point-deep;
    min-width: 24px;
    font-size: 12px;
  }

  &__choice-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__choice-text {
    font-size: 13px;
    color: $p-ink;
    line-height: 1.5;
  }

  &__choice-img {
    max-width: 100%;
    max-height: 200px;
    object-fit: contain;
    border-radius: $p-radius-sm;
    border: 1px solid $p-line;
    background: $p-panel;
  }

  &__choice-check {
    color: $p-ok;
    font-size: 18px !important;
    margin-left: auto;

    .edm__choice.has-image & {
      position: absolute;
      top: 8px;
      right: 8px;
    }
  }
}
</style>
